import React, { useMemo } from 'react';
import { FlexDiv, Image } from '../../design-system/containers';
import { Heading2 } from '../../design-system/headings';
import { TextLead } from '../../design-system/texts';
import { resourcePath } from '../../../utils/helpers';
import RadioButtonsGroup from '../radio-buttons-group';
import SelectsGroup from '../selects-group';
import { useTranslation } from 'react-i18next';

export const UpdatesCenterBlock = ({ ui, handleChange, value, answer }) => {
    const { t } = useTranslation('assessment');

    const onChange = (event, groupId) => {
        let val = value || [];
        val[groupId] = event.target.value;

        /** Current values or initialize the value array in the form we want*/
        let valueArray = !!value ? value : initializeValueArray();

        /** Find the group whose value we are updating, and update the value.
         * Example: Setting the value for 'check_for_updates' group to 'once_a_week' */
        const updatedData = valueArray.map((x) => {
            return x[groupId] !== undefined
                ? { ...x, [groupId]: event.target.value }
                : x;
        });

        handleChange(updatedData);
    };

    /** Init the value array to an object for each groupItem, with its actionId (groupId) as the key and empty string as value.
     * (in this case) [{"check_for_updates":""},{"install_updates":""},{"updates_from_websites":""}] */
    const initializeValueArray = () => {
        let group = ui.group || [];
        let valueArray = [];
        for (let i = 0; i < group.length; i++) {
            valueArray[i] = { [group[i].actionId]: '' };
        }
        return valueArray;
    };

    /** Simply find the 'check_for_updates' group definition in the JSON array*/
    const getCheckForUpdatesGroup = useMemo(
        () => ui.group.find((group) => group.actionId === 'check_for_updates'),
        []
    );

    const getInstallUpdatesGroup = useMemo(
        () => ui.group.find((group) => group.actionId === 'install_updates'),
        []
    );

    const getUpdatesFromWebsitesGroup = useMemo(
        () =>
            ui.group.find(
                (group) => group.actionId === 'updates_from_websites'
            ),
        []
    );

    return (
        <FlexDiv
            column
            backgroundColor="white"
            dropShadowAbove
            borderRadius={8}
            marginAuto
            overflowHidden
            maxWidthPercentage={80}
        >
            <FlexDiv
                column
                backgroundColor={'rgba(142, 191, 249, 0.1)'}
                padding={'48px 16px 24px 32'}
                positionRelative
            >
                <FlexDiv positionAbsolute right={'0'} top={'0'}>
                    <Image
                        src={resourcePath(`/static/img/update-cog-wheel.png`)}
                        alt={t('cog_wheel')}
                    />
                </FlexDiv>
                <Heading2>{t(ui.title)}</Heading2>
                <TextLead lighter>{t(ui.description)}</TextLead>
            </FlexDiv>
            <FlexDiv column px={32} py={24}>
                <FlexDiv column mb={8}>
                    <RadioButtonsGroup
                        groupId={'check_for_updates'}
                        radioOptions={getCheckForUpdatesGroup.options}
                        onChange={onChange}
                        value={
                            value && value[0]['check_for_updates']
                        } /* Index is 0 because this is the first group in this form */
                        answer={
                            answer && {
                                correctAnswer:
                                    answer.correctAnswer &&
                                    answer.correctAnswer['check_for_updates'],
                                userAnswer:
                                    answer.userAnswer &&
                                    answer.userAnswer['check_for_updates'],
                            }
                        }
                        overwriteGlobalStyles
                        label={t(getCheckForUpdatesGroup.title)}
                    />
                </FlexDiv>
                <FlexDiv column mb={8}>
                    <SelectsGroup
                        groupId={'install_updates'}
                        selectOptions={getInstallUpdatesGroup.options}
                        onChange={onChange}
                        label={t(getInstallUpdatesGroup.title)}
                        value={
                            value && value[1]['install_updates']
                        } /* Index is 1 because this is the second group in this form */
                        answer={
                            answer && {
                                correctAnswer:
                                    answer.correctAnswer &&
                                    answer.correctAnswer['install_updates'],
                                userAnswer:
                                    answer.userAnswer &&
                                    answer.userAnswer['install_updates'],
                            }
                        }
                    />
                </FlexDiv>
                <FlexDiv column mt={8}>
                    <RadioButtonsGroup
                        groupId={'updates_from_websites'}
                        radioOptions={getUpdatesFromWebsitesGroup.options}
                        onChange={onChange}
                        value={
                            value && value[2]['updates_from_websites']
                        } /* Index is 2 because this is the third group in this form */
                        answer={
                            answer && {
                                correctAnswer:
                                    answer.correctAnswer &&
                                    answer.correctAnswer[
                                        'updates_from_websites'
                                    ],
                                userAnswer:
                                    answer.userAnswer &&
                                    answer.userAnswer['updates_from_websites'],
                            }
                        }
                        label={t(getUpdatesFromWebsitesGroup.title)}
                        overwriteGlobalStyles
                    />
                </FlexDiv>
            </FlexDiv>
        </FlexDiv>
    );
};
